import { media, font, color } from "@universityofmaryland/web-token-library";
import { objectWithClassName } from "../utilities/create/jss.js";
import "../node_modules/.pnpm/postcss@8.5.8/node_modules/postcss/lib/postcss.js";
import "../node_modules/.pnpm/postcss-js@4.1.0_postcss@8.5.8/node_modules/postcss-js/index.js";
import "../node_modules/.pnpm/postcss-nesting@13.0.2_postcss@8.5.8/node_modules/postcss-nesting/dist/index.js";
import "../_virtual/index.js";
const breakpointMedium = media.queries.medium.min;
const breakpointLarge = media.queries.large.min;
const breakpointDesktop = media.queries.desktop.min;
const base = {
  fontFamily: font.family["campaign"],
  fontStyle: "italic",
  fontWeight: font.weight["bold"]
};
const sizeExtraSmall = {
  fontSize: font.size["4xl"],
  letterSpacing: "0.02em",
  lineHeight: `0.94em`
};
const sizeSmall = {
  fontSize: font.size["5xl"],
  letterSpacing: "0.02em",
  lineHeight: `0.91em`
};
const maximumBase = {
  ...base,
  ...sizeSmall,
  textWrap: "pretty",
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["9xl"]} + 2vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: "120px",
    lineHeight: `0.9em`
  }
};
const extralargeBase = {
  ...base,
  ...sizeSmall,
  textWrap: "pretty",
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["5xl"]} + 4vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: font.size["10xl"],
    lineHeight: `0.91em`
  }
};
const largeBase = {
  ...base,
  fontSize: font.size["4xl"],
  letterSpacing: "0.02em",
  lineHeight: `0.91em`,
  textWrap: "pretty",
  [`@media (${breakpointMedium})`]: {
    fontSize: font.size["5xl"]
  },
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["5xl"]} + 2.66vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: font.size["9xl"],
    lineHeight: `0.91em`
  }
};
const mediumBase = {
  ...base,
  ...sizeSmall,
  textWrap: "pretty",
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["5xl"]} + 1.33vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    fontSize: font.size["7xl"],
    letterSpacing: "0.02em",
    lineHeight: `0.94em`
  }
};
const campaignSmallBase = {
  ...base,
  ...sizeExtraSmall,
  [`@media (${breakpointLarge})`]: {
    fontSize: `calc(${font.size["4xl"]} + 1.33vw)`
  },
  [`@media (${breakpointDesktop})`]: {
    ...sizeSmall
  }
};
const extraSmallBase = {
  ...base,
  ...sizeExtraSmall
};
function compose(size, options) {
  const { theme, color: explicitColor } = options || {};
  const sizes = {
    maximum: maximumBase,
    extralarge: extralargeBase,
    large: largeBase,
    medium: mediumBase,
    small: campaignSmallBase,
    extrasmall: extraSmallBase
  };
  const base2 = sizes[size];
  const finalColor = explicitColor || (theme === "dark" ? color.white : color.black);
  const composed = {
    ...base2,
    ...theme && {
      color: finalColor
    }
  };
  const classNameParts = ["umd-campaign", size];
  if (theme === "dark") classNameParts.push("dark");
  return objectWithClassName({
    ...composed,
    className: classNameParts.join("-")
  });
}
const maximum = compose("maximum");
const extralarge = compose("extralarge");
const large = compose("large");
const medium = compose("medium");
const small = compose("small");
const extraSmall = compose("extrasmall");
const fonts = {
  /**
   * Maximum size campaign typography.
   * @returns {JssObject} Maximum size campaign typography with responsive behavior.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.maximum
   * ```
   * @example
   * ```css
   * class="umd-campaign-maximum"
   * ```
   * @since 1.1.0
   */
  maximum: compose("maximum"),
  /**
   * Extra large size campaign typography.
   * @returns {JssObject} Extra large size campaign typography with responsive behavior.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.extraLarge
   * ```
   * @example
   * ```css
   * class="umd-campaign-extralarge"
   * ```
   * @since 1.1.0
   */
  extraLarge: compose("extralarge"),
  /**
   * Large size campaign typography.
   * @returns {JssObject} Large size campaign typography with responsive behavior.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.large
   * ```
   * @example
   * ```css
   * class="umd-campaign-large"
   * ```
   * @since 1.1.0
   */
  large: compose("large"),
  /**
   * Medium size campaign typography.
   * @returns {JssObject} Medium size campaign typography with responsive behavior.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.medium
   * ```
   * @example
   * ```css
   * class="umd-campaign-medium"
   * ```
   * @since 1.1.0
   */
  medium: compose("medium"),
  /**
   * Small size campaign typography.
   * @returns {JssObject} Small size campaign typography with responsive behavior.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.small
   * ```
   * @example
   * ```css
   * class="umd-campaign-small"
   * ```
   * @since 1.1.0
   */
  small: compose("small"),
  /**
   * Extra small size campaign typography.
   * @returns {JssObject} Extra small size campaign typography.
   * @example
   * ```typescript
   * import * as Styles from '@universityofmaryland/web-styles-library';
   * Styles.typography.campaign.fonts.extraSmall
   * ```
   * @example
   * ```css
   * class="umd-campaign-extrasmall"
   * ```
   * @since 1.1.0
   */
  extraSmall: compose("extrasmall")
};
export {
  compose,
  extraSmall,
  extralarge,
  fonts,
  large,
  maximum,
  medium,
  small
};
//# sourceMappingURL=campaign.js.map
